import React, { Fragment, useEffect, useState } from 'react';
import { Button, Col, Container, Input, Row, Table } from 'reactstrap';
import { toast } from "react-toastify";
import _ from 'lodash';
import { FilterSelect, ServiceThreadSpinner } from '.';
import { api, filterHelpers } from '../utils';
import { confirmAlert } from "react-confirm-alert";

export default function ManageDepartments() {
   const ALL_OPTS = { value: 0, label: "-- Select --" };
   const INIT_MODEL = { deptId: 0, deptName: "", isActive: true };
   const [dept, setDept] = useState(INIT_MODEL);
   const [deptList, setDeptList] = useState([]);
   const [filters, setFilters] = useState([
      { filterName: "dept", value: ALL_OPTS }
   ]);
   const [editDept, setEditDept] = useState("");
   const [newDept, setNewDept] = useState("");
   const [origDept, setOrigDept] = useState("");
   const [spin, setSpinner] = useState(true);

   useEffect(() => {
      runQuery();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      if (!dept || dept.deptId === 0)
         return; // dept was reset to initial state

      if (!editDept || editDept.length < 1)
         return; // Not editing

      if (origDept === dept.deptName)
         return; // No change made to department being edited

      if (dept.deptName.length < 3) { // Do we need this?
         toast.error("Department name must be at least 3 characters");
         return;
      }
      UpdateDept();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [dept])

   function ActivateDept() {
      if (!dept || dept.deptId === 0) {
         toast.error("Please select a department to activate");
         return;
      }

      setSpinner(true);
      api.post(`Repair/ActivateDepartment/${dept.deptId}`)
         .then((response) => {
            if (response.data.success) {
               toast.success("Department activated!")
               runQuery();
            } else {
               toast.error(response.data.message);
               setSpinner(false);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            setSpinner(false);
         });
   }

   function DeleteDept() {
      setSpinner(true);
      api.post(`Repair/DeleteDepartment/${dept.deptId}`)
         .then((response) => {
            if (response.data.success) {
               toast.success("Department was successfully deleted or made inactive!")
               runQuery();
            } else {
               toast.error(response.data.message);
               setSpinner(false);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            setSpinner(false);
         });
   }

   function onDeptChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      setNewDept(""); // Clear any lingering values
      if (newFilterValue.value === 0) {
         setDept(INIT_MODEL);
         setEditDept("");
         setOrigDept("");
      }
      else { // Get the department to be edited
         const d = _.find(deptList, (x) => x.value === newFilterValue.value);
         let changes = Object.assign({}, dept);
         changes.deptName = newFilterValue.label;
         changes.deptId = newFilterValue.value;
         changes.isActive = d.isActive;
         setDept(changes);
         setEditDept(newFilterValue.label);
         setOrigDept(newFilterValue.label);
      }
   }

   function onDeptUpdate() {
      let changes = Object.assign({}, dept);
      changes.deptName = editDept
      setDept(changes);
   }

   function runQuery() {
      setSpinner(true);
      api
         .fetch(`Repair/LoadAllDepartments`)
         .then((response) => {
            if (response.data.success) {
               const dptList = _.concat([ALL_OPTS],
                  _.map(response.data.departments, (d) => ({
                     value: d.deptId,
                     label: d.deptName,
                     isActive: d.isActive
                  })));
               return { dptList: dptList };
            } else {
               toast.error(response.data.message);
            }
         })
         .then(({ dptList }) => {
            setDeptList(dptList);
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setEditDept("");
            setOrigDept("");
            setNewDept("")
            setDept(INIT_MODEL);
            setSpinner(false);
         });
   }

   function SaveNewDept() {
      if (!newDept || newDept.length < 1) {
         toast.error("Please provide a department name");
         return;
      }
      if (newDept.length < 3) {
         toast.error("Department name must be at least 3 characters");
         return;
      }
      setSpinner(true);
      api.post('Repair/SaveDepartment', newDept)
         .then((response) => {
            if (response.data.success) {
               toast.success("Department created!")
               runQuery();
            } else {
               toast.error(response.data.message);
               setSpinner(false);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            setSpinner(false);
         });
   }

   function UpdateDept() {
      setSpinner(true);
      api.post('Repair/UpdateDepartment', dept)
         .then((response) => {
            if (response.data.success) {
               toast.success("Department updated!")
               runQuery();
            } else {
               toast.error(response.data.message);
               setSpinner(false);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            setSpinner(false);
         });
   }

   const ShowDeleteModal = () => {
      if (!dept || dept.deptId === 0) {
         toast.error("Please select a department");
         return;
      }
      confirmAlert({
         title: "Confirm Deletion or Inactivation",
         message: `Are you sure you want to delete the ${editDept} department?`,
         buttons: [
            { label: "Yes", onClick: () => { DeleteDept() }},
            { label: "No" }
         ]
      });
   }

   return (
      <Container fluid>
         <Row>
            <Col>
               <Fragment>
                  <ServiceThreadSpinner on={spin}>
                     <Row className="px-2 py-2">
                        <Col xxl={{ offset: 0, size: 12 }}>
                           {deptList && deptList.length ? (
                              <Table className="transparent-background my-0">
                                 <tbody>
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6" width="80px">
                                          Dept:
                                       </td>
                                       <td className="transparent-background" width="500px">
                                          <FilterSelect
                                             filterName="dept"
                                             options={deptList}
                                             value={filterHelpers.getValue(filters, "dept")}
                                             onChangeCallback={e => onDeptChange(e)}
                                          />
                                       </td>
                                       <td className="transparent-background"></td>
                                    </tr>
                                 </tbody>
                              </Table>
                           ) : null}
                        </Col>
                     </Row>

                     {dept && dept.deptId > 0 ? (
                        <div>
                           <Row className="px-0 py-0">
                              <Table className="transparent-background data-table">
                                 <tbody>
                                    <tr>
                                       <td className="transparent-background data-cell width40px"></td>
                                       <td colSpan={5} className="transparent-background data-cell noWrap h6">
                                          Deleting a department that has machines assigned to it will set it to inactive.
                                       </td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell width40px"></td>
                                       <td colSpan={5} className="transparent-background data-cell noWrap h6">
                                          Inactive departments will not show up in production record search options.
                                       </td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell width40px"></td>
                                       <td className="transparent-background data-cell width95px noWrap h6">
                                          Id: {dept.deptId}
                                       </td>
                                       <td className="transparent-background data-cell noWrap h6 width115px">
                                          Active: {dept.isActive ? "Yes" : "No"}
                                       </td>
                                       <td colSpan={3} className="transparent-background data-cell"></td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell width40px"></td>
                                       <td className="transparent-background data-cell noWrap h6 width115px">
                                          Dept. Name:
                                       </td>
                                       <td className="transparent-background data-cell width500px">
                                          <Input
                                             type="text"
                                             value={editDept || ""}
                                             onChange={(e) => setEditDept(e.target.value)}
                                             maxLength="100"
                                          />
                                       </td>
                                       <td className="transparent-background data-cell width115px">
                                          <Button
                                             title="Update"
                                             className="shredButton"
                                             onClick={() => onDeptUpdate()}
                                          >
                                             Update
                                          </Button>
                                       </td>
                                       <td className="transparent-background data-cell width115px">
                                          <Button
                                             title="Delete"
                                             className="shredButton"
                                             onClick={() => ShowDeleteModal()}
                                          >
                                             Delete
                                          </Button>
                                       </td>
                                       {!dept.isActive ? (
                                          <td className="transparent-background data-cell">
                                             <Button
                                                title="Reactivate"
                                                className="shredButton"
                                                onClick={() => ActivateDept()}
                                             >
                                                Reactivate
                                             </Button>
                                          </td>
                                       ) : <td className="transparent-background data-cell"></td>}
                                    </tr>
                                 </tbody>
                              </Table>
                           </Row>
                        </div>
                     ) : null}

                     <br />
                     <br />
                     <hr />
                     <Row className="px-2 py-2">
                        <Col xxl={{ offset: 0, size: 12 }}>
                           Add New Department
                        </Col>
                     </Row>
                     <Row className="px-2 py-2">
                        <Col xxl={{ offset: 0, size: 12 }}>
                           <Table className="transparent-background my-0">
                              <tbody>
                                 <tr>
                                    <td className="transparent-background data-cell noWrap h6" width="80px">
                                       Name:
                                    </td>
                                    <td className="transparent-background" width="500px">
                                       <Input
                                          type="text"
                                          value={newDept || ""}
                                          onChange={(e) => setNewDept(e.target.value)}
                                          maxLength="100"
                                       />
                                    </td>
                                    <td className="transparent-background">
                                       <Button
                                          title="New Dept"
                                          className="shredButtonExcel ms-2"
                                          onClick={() => SaveNewDept()}
                                       >
                                          Add Department
                                       </Button>
                                    </td>
                                 </tr>
                              </tbody>
                           </Table>
                        </Col>
                     </Row>
                  </ServiceThreadSpinner>
               </Fragment>
            </Col>
         </Row>
      </Container>
   );
}
